import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import { ServicesBaseUrl } from '@garmin-avcloud/avcloud-fly-web-common/_shared';
import { FLY_WEB_COMMON_CONFIG } from '@garmin-avcloud/avcloud-fly-web-common/config';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpContext, HttpParams } from '@angular/common/http';

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
class CustomHttpParameterCodec {
  encodeKey(k) {
    return encodeURIComponent(k);
  }
  encodeValue(v) {
    return encodeURIComponent(v);
  }
  decodeKey(k) {
    return decodeURIComponent(k);
  }
  decodeValue(v) {
    return decodeURIComponent(v);
  }
}
const BASE_PATH = new InjectionToken('basePath');
const COLLECTION_FORMATS = {
  'csv': ',',
  'tsv': '   ',
  'ssv': ' ',
  'pipes': '|'
};
class TelephonyDomainConfiguration {
  constructor(configurationParameters = {}) {
    this.apiKeys = configurationParameters.apiKeys;
    this.username = configurationParameters.username;
    this.password = configurationParameters.password;
    this.accessToken = configurationParameters.accessToken;
    this.basePath = configurationParameters.basePath;
    this.withCredentials = configurationParameters.withCredentials;
    this.encoder = configurationParameters.encoder;
    if (configurationParameters.encodeParam) {
      this.encodeParam = configurationParameters.encodeParam;
    } else {
      this.encodeParam = param => this.defaultEncodeParam(param);
    }
    if (configurationParameters.credentials) {
      this.credentials = configurationParameters.credentials;
    } else {
      this.credentials = {};
    }
    // init default bearerAuth credential
    if (!this.credentials['bearerAuth']) {
      this.credentials['bearerAuth'] = () => {
        return typeof this.accessToken === 'function' ? this.accessToken() : this.accessToken;
      };
    }
  }
  /**
   * Select the correct content-type to use for a request.
   * Uses {@link TelephonyDomainConfiguration#isJsonMime} to determine the correct content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param contentTypes - the array of content types that are available for selection
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderContentType(contentTypes) {
    if (contentTypes.length === 0) {
      return undefined;
    }
    const type = contentTypes.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return contentTypes[0];
    }
    return type;
  }
  /**
   * Select the correct accept content-type to use for a request.
   * Uses {@link TelephonyDomainConfiguration#isJsonMime} to determine the correct accept content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param accepts - the array of content types that are available for selection.
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderAccept(accepts) {
    if (accepts.length === 0) {
      return undefined;
    }
    const type = accepts.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return accepts[0];
    }
    return type;
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    const jsonMime = new RegExp('^(application\/json|[^;/ \t]+\/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
    return mime !== null && (jsonMime.test(mime) || mime.toLowerCase() === 'application/json-patch+json');
  }
  lookupCredential(key) {
    const value = this.credentials[key];
    return typeof value === 'function' ? value() : value;
  }
  defaultEncodeParam(param) {
    // This implementation exists as fallback for missing configuration
    // and for backwards compatibility to older typescript-angular generator versions.
    // It only works for the 'simple' parameter style.
    // Date-handling only works for the 'date-time' format.
    // All other styles and Date-formats are probably handled incorrectly.
    //
    // But: if that's all you need (i.e.: the most common use-case): no need for customization!
    const value = param.dataFormat === 'date-time' && param.value instanceof Date ? param.value.toISOString() : param.value;
    return encodeURIComponent(String(value));
  }
}

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CountryControllerService = /*#__PURE__*/(() => {
  class CountryControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addCountry(countryPrivate, observe = 'body', reportProgress = false, options) {
      if (countryPrivate === null || countryPrivate === undefined) {
        throw new Error('Required parameter countryPrivate was null or undefined when calling addCountry.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/countries`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: countryPrivate,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteCountry(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteCountry.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/countries/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCountry(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getCountry.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/countries/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCountryByIsoCode(isoCode, observe = 'body', reportProgress = false, options) {
      if (isoCode === null || isoCode === undefined) {
        throw new Error('Required parameter isoCode was null or undefined when calling getCountryByIsoCode.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/countries/by-iso-code/${this.configuration.encodeParam({
        name: "isoCode",
        value: isoCode,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCountryList(includeInactive, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (includeInactive !== undefined && includeInactive !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeInactive, 'includeInactive');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/countries`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCountryListPublic(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/countries/public`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateCountry(id, countryPrivate, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateCountry.');
      }
      if (countryPrivate === null || countryPrivate === undefined) {
        throw new Error('Required parameter countryPrivate was null or undefined when calling updateCountry.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/countries/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: countryPrivate,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CountryControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CountryControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CountryControllerService,
        factory: CountryControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CountryControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EmailContactControllerService = /*#__PURE__*/(() => {
  class EmailContactControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addEmailContact(emailContact, observe = 'body', reportProgress = false, options) {
      if (emailContact === null || emailContact === undefined) {
        throw new Error('Required parameter emailContact was null or undefined when calling addEmailContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-contacts`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: emailContact,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEmailContact(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteEmailContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-contacts/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEmailContact(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getEmailContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-contacts/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOrCreateEmailContact(emailAddress, observe = 'body', reportProgress = false, options) {
      if (emailAddress === null || emailAddress === undefined) {
        throw new Error('Required parameter emailAddress was null or undefined when calling getOrCreateEmailContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-contacts/get-or-create/${this.configuration.encodeParam({
        name: "emailAddress",
        value: emailAddress,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateEmailContact(id, emailContact, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateEmailContact.');
      }
      if (emailContact === null || emailContact === undefined) {
        throw new Error('Required parameter emailContact was null or undefined when calling updateEmailContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-contacts/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: emailContact,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EmailContactControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EmailContactControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EmailContactControllerService,
        factory: EmailContactControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EmailContactControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EmailMessageControllerService = /*#__PURE__*/(() => {
  class EmailMessageControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addEmailMessage(emailMessage, observe = 'body', reportProgress = false, options) {
      if (emailMessage === null || emailMessage === undefined) {
        throw new Error('Required parameter emailMessage was null or undefined when calling addEmailMessage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-messages`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: emailMessage,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEmailMessage(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteEmailMessage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-messages/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEmailMessage(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getEmailMessage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-messages/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    ingestMOEmailMessage(emailMessageMORequest, testing, observe = 'body', reportProgress = false, options) {
      if (emailMessageMORequest === null || emailMessageMORequest === undefined) {
        throw new Error('Required parameter emailMessageMORequest was null or undefined when calling ingestMOEmailMessage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (testing !== undefined && testing !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, testing, 'testing');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-messages/ingest-mo-message`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: emailMessageMORequest,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    ingestMTEmailMessage(emailMessageMTRequest, testing, observe = 'body', reportProgress = false, options) {
      if (emailMessageMTRequest === null || emailMessageMTRequest === undefined) {
        throw new Error('Required parameter emailMessageMTRequest was null or undefined when calling ingestMTEmailMessage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (testing !== undefined && testing !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, testing, 'testing');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-messages/ingest-mt-message`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: emailMessageMTRequest,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchEmailMessages(emailMessageSearchCriteria, observe = 'body', reportProgress = false, options) {
      if (emailMessageSearchCriteria === null || emailMessageSearchCriteria === undefined) {
        throw new Error('Required parameter emailMessageSearchCriteria was null or undefined when calling searchEmailMessages.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-messages/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: emailMessageSearchCriteria,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateEmailMessage(id, emailMessage, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateEmailMessage.');
      }
      if (emailMessage === null || emailMessage === undefined) {
        throw new Error('Required parameter emailMessage was null or undefined when calling updateEmailMessage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/email-messages/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: emailMessage,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EmailMessageControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EmailMessageControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EmailMessageControllerService,
        factory: EmailMessageControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EmailMessageControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let LocalNumberControllerService = /*#__PURE__*/(() => {
  class LocalNumberControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addLocalNumber(localNumber, observe = 'body', reportProgress = false, options) {
      if (localNumber === null || localNumber === undefined) {
        throw new Error('Required parameter localNumber was null or undefined when calling addLocalNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localNumber,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteLocalNumber(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteLocalNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getIridiumNumberForLocalNumber(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getIridiumNumberForLocalNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/iridiumNumber`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLocalNumber(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getLocalNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLocalNumberByImei(imei, observe = 'body', reportProgress = false, options) {
      if (imei === null || imei === undefined) {
        throw new Error('Required parameter imei was null or undefined when calling getLocalNumberByImei.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/by-imei/${this.configuration.encodeParam({
        name: "imei",
        value: imei,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLocalNumberByMasterAircraftUuid(masterAircraftUUID, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUUID === null || masterAircraftUUID === undefined) {
        throw new Error('Required parameter masterAircraftUUID was null or undefined when calling getLocalNumberByMasterAircraftUuid.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/by-master-aircraft-uuid/${this.configuration.encodeParam({
        name: "masterAircraftUUID",
        value: masterAircraftUUID,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLocalNumberByNumber(number, observe = 'body', reportProgress = false, options) {
      if (number === null || number === undefined) {
        throw new Error('Required parameter number was null or undefined when calling getLocalNumberByNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/by-number/${this.configuration.encodeParam({
        name: "number",
        value: number,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLocalNumberDetailedByMasterAircraftUuid(masterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUuid === null || masterAircraftUuid === undefined) {
        throw new Error('Required parameter masterAircraftUuid was null or undefined when calling getLocalNumberDetailedByMasterAircraftUuid.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/by-master-aircraft-uuid/${this.configuration.encodeParam({
        name: "masterAircraftUuid",
        value: masterAircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/detailed`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLocalNumberDetailedByMasterAircraftUuidPublic(masterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUuid === null || masterAircraftUuid === undefined) {
        throw new Error('Required parameter masterAircraftUuid was null or undefined when calling getLocalNumberDetailedByMasterAircraftUuidPublic.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/by-master-aircraft-uuid/${this.configuration.encodeParam({
        name: "masterAircraftUuid",
        value: masterAircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/detailed/public`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMasterAircraftForLocalNumber(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getMasterAircraftForLocalNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/master-aircraft`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    provisionLocalNumber(imei, countryId, areaCode, test, observe = 'body', reportProgress = false, options) {
      if (imei === null || imei === undefined) {
        throw new Error('Required parameter imei was null or undefined when calling provisionLocalNumber.');
      }
      if (countryId === null || countryId === undefined) {
        throw new Error('Required parameter countryId was null or undefined when calling provisionLocalNumber.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (countryId !== undefined && countryId !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, countryId, 'countryId');
      }
      if (areaCode !== undefined && areaCode !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, areaCode, 'areaCode');
      }
      if (test !== undefined && test !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, test, 'test');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/provision/${this.configuration.encodeParam({
        name: "imei",
        value: imei,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    provisionLocalNumberDetailedByMasterAircraftUuidPublic(masterAircraftUuid, countryId, areaCode, test, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUuid === null || masterAircraftUuid === undefined) {
        throw new Error('Required parameter masterAircraftUuid was null or undefined when calling provisionLocalNumberDetailedByMasterAircraftUuidPublic.');
      }
      if (countryId === null || countryId === undefined) {
        throw new Error('Required parameter countryId was null or undefined when calling provisionLocalNumberDetailedByMasterAircraftUuidPublic.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (countryId !== undefined && countryId !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, countryId, 'countryId');
      }
      if (areaCode !== undefined && areaCode !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, areaCode, 'areaCode');
      }
      if (test !== undefined && test !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, test, 'test');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/by-master-aircraft-uuid/${this.configuration.encodeParam({
        name: "masterAircraftUuid",
        value: masterAircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/detailed/public/provision`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    releaseLocalNumber(imei, observe = 'body', reportProgress = false, options) {
      if (imei === null || imei === undefined) {
        throw new Error('Required parameter imei was null or undefined when calling releaseLocalNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/release/${this.configuration.encodeParam({
        name: "imei",
        value: imei,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    releaseLocalNumberByMasterAircraft(masterAircraftUuid, test, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUuid === null || masterAircraftUuid === undefined) {
        throw new Error('Required parameter masterAircraftUuid was null or undefined when calling releaseLocalNumberByMasterAircraft.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (test !== undefined && test !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, test, 'test');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/by-master-aircraft-uuid/${this.configuration.encodeParam({
        name: "masterAircraftUuid",
        value: masterAircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/detailed/public/release`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateLocalNumber(id, localNumber, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateLocalNumber.');
      }
      if (localNumber === null || localNumber === undefined) {
        throw new Error('Required parameter localNumber was null or undefined when calling updateLocalNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-numbers/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localNumber,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function LocalNumberControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LocalNumberControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LocalNumberControllerService,
        factory: LocalNumberControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LocalNumberControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let LocalNumberOverrideControllerService = /*#__PURE__*/(() => {
  class LocalNumberOverrideControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addLocalNumberOverride(localNumberOverride, observe = 'body', reportProgress = false, options) {
      if (localNumberOverride === null || localNumberOverride === undefined) {
        throw new Error('Required parameter localNumberOverride was null or undefined when calling addLocalNumberOverride.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-number-overrides`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localNumberOverride,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteLocalNumberOverride(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteLocalNumberOverride.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-number-overrides/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLocalNumberOverride(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getLocalNumberOverride.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-number-overrides/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLocalNumberOverrideByLocalNumberId(localNumberId, observe = 'body', reportProgress = false, options) {
      if (localNumberId === null || localNumberId === undefined) {
        throw new Error('Required parameter localNumberId was null or undefined when calling getLocalNumberOverrideByLocalNumberId.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-number-overrides/by-local-number/${this.configuration.encodeParam({
        name: "localNumberId",
        value: localNumberId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateLocalNumberOverride(id, localNumberOverride, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateLocalNumberOverride.');
      }
      if (localNumberOverride === null || localNumberOverride === undefined) {
        throw new Error('Required parameter localNumberOverride was null or undefined when calling updateLocalNumberOverride.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/local-number-overrides/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localNumberOverride,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function LocalNumberOverrideControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LocalNumberOverrideControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LocalNumberOverrideControllerService,
        factory: LocalNumberOverrideControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LocalNumberOverrideControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MessageDirectionControllerService = /*#__PURE__*/(() => {
  class MessageDirectionControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAllMessageDirections(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/message-directions`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMessageDirection(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getMessageDirection.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/message-directions/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMessageDirectionByCode(code, observe = 'body', reportProgress = false, options) {
      if (code === null || code === undefined) {
        throw new Error('Required parameter code was null or undefined when calling getMessageDirectionByCode.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/message-directions/by-code/${this.configuration.encodeParam({
        name: "code",
        value: code,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MessageDirectionControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MessageDirectionControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MessageDirectionControllerService,
        factory: MessageDirectionControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MessageDirectionControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MoVoicePinControllerService = /*#__PURE__*/(() => {
  class MoVoicePinControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createMoVoicePin(moVoicePin, observe = 'body', reportProgress = false, options) {
      if (moVoicePin === null || moVoicePin === undefined) {
        throw new Error('Required parameter moVoicePin was null or undefined when calling createMoVoicePin.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/mo-voice-pins`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: moVoicePin,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMoVoicePin(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteMoVoicePin.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/mo-voice-pins/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMoVoicePin(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getMoVoicePin.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/mo-voice-pins/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMoVoicePinByLocalNumberIdAndPin(localNumberId, pin, observe = 'body', reportProgress = false, options) {
      if (localNumberId === null || localNumberId === undefined) {
        throw new Error('Required parameter localNumberId was null or undefined when calling getMoVoicePinByLocalNumberIdAndPin.');
      }
      if (pin === null || pin === undefined) {
        throw new Error('Required parameter pin was null or undefined when calling getMoVoicePinByLocalNumberIdAndPin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (localNumberId !== undefined && localNumberId !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, localNumberId, 'localNumberId');
      }
      if (pin !== undefined && pin !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pin, 'pin');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/mo-voice-pins`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOrCreatePin(localNumberId, observe = 'body', reportProgress = false, options) {
      if (localNumberId === null || localNumberId === undefined) {
        throw new Error('Required parameter localNumberId was null or undefined when calling getOrCreatePin.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/mo-voice-pins/get-or-create-mo-voice-pin/${this.configuration.encodeParam({
        name: "localNumberId",
        value: localNumberId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateMoVoicePin(id, moVoicePin, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateMoVoicePin.');
      }
      if (moVoicePin === null || moVoicePin === undefined) {
        throw new Error('Required parameter moVoicePin was null or undefined when calling updateMoVoicePin.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/mo-voice-pins/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: moVoicePin,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MoVoicePinControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MoVoicePinControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MoVoicePinControllerService,
        factory: MoVoicePinControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MoVoicePinControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OptInStatusTypeControllerService = /*#__PURE__*/(() => {
  class OptInStatusTypeControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAllOptInStatusTypes(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/opt-in-status-types`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOptInStatusType(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getOptInStatusType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/opt-in-status-types/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOptInStatusTypeByCode(code, observe = 'body', reportProgress = false, options) {
      if (code === null || code === undefined) {
        throw new Error('Required parameter code was null or undefined when calling getOptInStatusTypeByCode.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/opt-in-status-types/by-code/${this.configuration.encodeParam({
        name: "code",
        value: code,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OptInStatusTypeControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OptInStatusTypeControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OptInStatusTypeControllerService,
        factory: OptInStatusTypeControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OptInStatusTypeControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PhoneContactControllerService = /*#__PURE__*/(() => {
  class PhoneContactControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addPhoneContact(phoneContact, observe = 'body', reportProgress = false, options) {
      if (phoneContact === null || phoneContact === undefined) {
        throw new Error('Required parameter phoneContact was null or undefined when calling addPhoneContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: phoneContact,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deletePhoneContact(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deletePhoneContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOrCreatePhoneContact(phoneNumber, observe = 'body', reportProgress = false, options) {
      if (phoneNumber === null || phoneNumber === undefined) {
        throw new Error('Required parameter phoneNumber was null or undefined when calling getOrCreatePhoneContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts/get-or-create/${this.configuration.encodeParam({
        name: "phoneNumber",
        value: phoneNumber,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPhoneContact(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getPhoneContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updatePhoneContact(id, phoneContact, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updatePhoneContact.');
      }
      if (phoneContact === null || phoneContact === undefined) {
        throw new Error('Required parameter phoneContact was null or undefined when calling updatePhoneContact.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: phoneContact,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PhoneContactControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PhoneContactControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PhoneContactControllerService,
        factory: PhoneContactControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PhoneContactControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PhoneContactPreferenceControllerService = /*#__PURE__*/(() => {
  class PhoneContactPreferenceControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addPhoneContactPreference(phoneContactId, localNumberId, phoneContactPreferenceRequest, observe = 'body', reportProgress = false, options) {
      if (phoneContactId === null || phoneContactId === undefined) {
        throw new Error('Required parameter phoneContactId was null or undefined when calling addPhoneContactPreference.');
      }
      if (localNumberId === null || localNumberId === undefined) {
        throw new Error('Required parameter localNumberId was null or undefined when calling addPhoneContactPreference.');
      }
      if (phoneContactPreferenceRequest === null || phoneContactPreferenceRequest === undefined) {
        throw new Error('Required parameter phoneContactPreferenceRequest was null or undefined when calling addPhoneContactPreference.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts/${this.configuration.encodeParam({
        name: "phoneContactId",
        value: phoneContactId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/preferences/${this.configuration.encodeParam({
        name: "localNumberId",
        value: localNumberId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: phoneContactPreferenceRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deletePhoneContactPreference(phoneContactId, localNumberId, observe = 'body', reportProgress = false, options) {
      if (phoneContactId === null || phoneContactId === undefined) {
        throw new Error('Required parameter phoneContactId was null or undefined when calling deletePhoneContactPreference.');
      }
      if (localNumberId === null || localNumberId === undefined) {
        throw new Error('Required parameter localNumberId was null or undefined when calling deletePhoneContactPreference.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts/${this.configuration.encodeParam({
        name: "phoneContactId",
        value: phoneContactId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/preferences/${this.configuration.encodeParam({
        name: "localNumberId",
        value: localNumberId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPhoneContactPreference(phoneContactId, localNumberId, observe = 'body', reportProgress = false, options) {
      if (phoneContactId === null || phoneContactId === undefined) {
        throw new Error('Required parameter phoneContactId was null or undefined when calling getPhoneContactPreference.');
      }
      if (localNumberId === null || localNumberId === undefined) {
        throw new Error('Required parameter localNumberId was null or undefined when calling getPhoneContactPreference.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts/${this.configuration.encodeParam({
        name: "phoneContactId",
        value: phoneContactId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/preferences/${this.configuration.encodeParam({
        name: "localNumberId",
        value: localNumberId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPhoneContactPreferenceHistory(phoneContactId, localNumberId, observe = 'body', reportProgress = false, options) {
      if (phoneContactId === null || phoneContactId === undefined) {
        throw new Error('Required parameter phoneContactId was null or undefined when calling getPhoneContactPreferenceHistory.');
      }
      if (localNumberId === null || localNumberId === undefined) {
        throw new Error('Required parameter localNumberId was null or undefined when calling getPhoneContactPreferenceHistory.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts/${this.configuration.encodeParam({
        name: "phoneContactId",
        value: phoneContactId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/preferences/${this.configuration.encodeParam({
        name: "localNumberId",
        value: localNumberId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/history`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updatePhoneContactPreference(phoneContactId, localNumberId, phoneContactPreferenceRequest, observe = 'body', reportProgress = false, options) {
      if (phoneContactId === null || phoneContactId === undefined) {
        throw new Error('Required parameter phoneContactId was null or undefined when calling updatePhoneContactPreference.');
      }
      if (localNumberId === null || localNumberId === undefined) {
        throw new Error('Required parameter localNumberId was null or undefined when calling updatePhoneContactPreference.');
      }
      if (phoneContactPreferenceRequest === null || phoneContactPreferenceRequest === undefined) {
        throw new Error('Required parameter phoneContactPreferenceRequest was null or undefined when calling updatePhoneContactPreference.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-contacts/${this.configuration.encodeParam({
        name: "phoneContactId",
        value: phoneContactId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/preferences/${this.configuration.encodeParam({
        name: "localNumberId",
        value: localNumberId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: phoneContactPreferenceRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PhoneContactPreferenceControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PhoneContactPreferenceControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PhoneContactPreferenceControllerService,
        factory: PhoneContactPreferenceControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PhoneContactPreferenceControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PhoneNumberTypeControllerService = /*#__PURE__*/(() => {
  class PhoneNumberTypeControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAllPhoneNumberTypes(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-number-types`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPhoneNumberType(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getPhoneNumberType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-number-types/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPhoneNumberTypeByCode(code, observe = 'body', reportProgress = false, options) {
      if (code === null || code === undefined) {
        throw new Error('Required parameter code was null or undefined when calling getPhoneNumberTypeByCode.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/phone-number-types/by-code/${this.configuration.encodeParam({
        name: "code",
        value: code,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PhoneNumberTypeControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PhoneNumberTypeControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PhoneNumberTypeControllerService,
        factory: PhoneNumberTypeControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PhoneNumberTypeControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SettingControllerService = /*#__PURE__*/(() => {
  class SettingControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addSetting(setting, observe = 'body', reportProgress = false, options) {
      if (setting === null || setting === undefined) {
        throw new Error('Required parameter setting was null or undefined when calling addSetting.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/settings`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: setting,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteSetting(identifier, observe = 'body', reportProgress = false, options) {
      if (identifier === null || identifier === undefined) {
        throw new Error('Required parameter identifier was null or undefined when calling deleteSetting.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/settings/${this.configuration.encodeParam({
        name: "identifier",
        value: identifier,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSetting(identifier, observe = 'body', reportProgress = false, options) {
      if (identifier === null || identifier === undefined) {
        throw new Error('Required parameter identifier was null or undefined when calling getSetting.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/settings/${this.configuration.encodeParam({
        name: "identifier",
        value: identifier,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSettingByProperty(property, observe = 'body', reportProgress = false, options) {
      if (property === null || property === undefined) {
        throw new Error('Required parameter property was null or undefined when calling getSettingByProperty.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/settings/by-property/${this.configuration.encodeParam({
        name: "property",
        value: property,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSettings(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/settings`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateSetting(identifier, setting, observe = 'body', reportProgress = false, options) {
      if (identifier === null || identifier === undefined) {
        throw new Error('Required parameter identifier was null or undefined when calling updateSetting.');
      }
      if (setting === null || setting === undefined) {
        throw new Error('Required parameter setting was null or undefined when calling updateSetting.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/settings/${this.configuration.encodeParam({
        name: "identifier",
        value: identifier,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: setting,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SettingControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SettingControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SettingControllerService,
        factory: SettingControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SettingControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SmsMessageControllerService = /*#__PURE__*/(() => {
  class SmsMessageControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addSmsMessage(smsMessage, observe = 'body', reportProgress = false, options) {
      if (smsMessage === null || smsMessage === undefined) {
        throw new Error('Required parameter smsMessage was null or undefined when calling addSmsMessage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/sms-messages`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: smsMessage,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteSmsMessage(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteSmsMessage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/sms-messages/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSmsMessage(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getSmsMessage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/sms-messages/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSmsMessageByExternalMessageId(externalMessageId, observe = 'body', reportProgress = false, options) {
      if (externalMessageId === null || externalMessageId === undefined) {
        throw new Error('Required parameter externalMessageId was null or undefined when calling getSmsMessageByExternalMessageId.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/sms-messages/by-external-message-id/${this.configuration.encodeParam({
        name: "externalMessageId",
        value: externalMessageId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    ingestMOMessage(smsMessageMORequest, testing, observe = 'body', reportProgress = false, options) {
      if (smsMessageMORequest === null || smsMessageMORequest === undefined) {
        throw new Error('Required parameter smsMessageMORequest was null or undefined when calling ingestMOMessage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (testing !== undefined && testing !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, testing, 'testing');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/sms-messages/ingest-mo-message`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: smsMessageMORequest,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    ingestMTMessage(smsMessageMTRequest, testing, observe = 'body', reportProgress = false, options) {
      if (smsMessageMTRequest === null || smsMessageMTRequest === undefined) {
        throw new Error('Required parameter smsMessageMTRequest was null or undefined when calling ingestMTMessage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (testing !== undefined && testing !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, testing, 'testing');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/sms-messages/ingest-mt-message`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: smsMessageMTRequest,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchSmsMessages(smsMessageSearchCriteria, observe = 'body', reportProgress = false, options) {
      if (smsMessageSearchCriteria === null || smsMessageSearchCriteria === undefined) {
        throw new Error('Required parameter smsMessageSearchCriteria was null or undefined when calling searchSmsMessages.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/sms-messages/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: smsMessageSearchCriteria,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateSmsMessage(id, smsMessage, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateSmsMessage.');
      }
      if (smsMessage === null || smsMessage === undefined) {
        throw new Error('Required parameter smsMessage was null or undefined when calling updateSmsMessage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/sms-messages/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: smsMessage,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SmsMessageControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SmsMessageControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SmsMessageControllerService,
        factory: SmsMessageControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SmsMessageControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ThirdPartyNumberControllerService = /*#__PURE__*/(() => {
  class ThirdPartyNumberControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addThirdPartyNumber(thirdPartyNumber, observe = 'body', reportProgress = false, options) {
      if (thirdPartyNumber === null || thirdPartyNumber === undefined) {
        throw new Error('Required parameter thirdPartyNumber was null or undefined when calling addThirdPartyNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/third-party-numbers`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: thirdPartyNumber,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteThirdPartyNumber(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteThirdPartyNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/third-party-numbers/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExpiredThirdPartyNumbers(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/third-party-numbers/expired`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getThirdPartyNumber(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getThirdPartyNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/third-party-numbers/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateThirdPartyNumber(id, thirdPartyNumber, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateThirdPartyNumber.');
      }
      if (thirdPartyNumber === null || thirdPartyNumber === undefined) {
        throw new Error('Required parameter thirdPartyNumber was null or undefined when calling updateThirdPartyNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/third-party-numbers/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: thirdPartyNumber,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ThirdPartyNumberControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ThirdPartyNumberControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ThirdPartyNumberControllerService,
        factory: ThirdPartyNumberControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ThirdPartyNumberControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let VoiceCallControllerService = /*#__PURE__*/(() => {
  class VoiceCallControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createVoiceCall(voiceCall, observe = 'body', reportProgress = false, options) {
      if (voiceCall === null || voiceCall === undefined) {
        throw new Error('Required parameter voiceCall was null or undefined when calling createVoiceCall.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-calls`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: voiceCall,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteVoiceCall(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteVoiceCall.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-calls/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVoiceCall(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getVoiceCall.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-calls/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVoiceCallByOriginationId(originationId, observe = 'body', reportProgress = false, options) {
      if (originationId === null || originationId === undefined) {
        throw new Error('Required parameter originationId was null or undefined when calling getVoiceCallByOriginationId.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-calls/by-origination-id/${this.configuration.encodeParam({
        name: "originationId",
        value: originationId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchVoiceCalls(voiceCallSearchCriteria, observe = 'body', reportProgress = false, options) {
      if (voiceCallSearchCriteria === null || voiceCallSearchCriteria === undefined) {
        throw new Error('Required parameter voiceCallSearchCriteria was null or undefined when calling searchVoiceCalls.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-calls/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: voiceCallSearchCriteria,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateVoiceCall(id, voiceCall, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateVoiceCall.');
      }
      if (voiceCall === null || voiceCall === undefined) {
        throw new Error('Required parameter voiceCall was null or undefined when calling updateVoiceCall.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-calls/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: voiceCall,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function VoiceCallControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || VoiceCallControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: VoiceCallControllerService,
        factory: VoiceCallControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return VoiceCallControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let VoiceCallDispositionControllerService = /*#__PURE__*/(() => {
  class VoiceCallDispositionControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-api-dev.garmin.com/telephonyDomainService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new TelephonyDomainConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    addVoiceCallDisposition(voiceCallDisposition, observe = 'body', reportProgress = false, options) {
      if (voiceCallDisposition === null || voiceCallDisposition === undefined) {
        throw new Error('Required parameter voiceCallDisposition was null or undefined when calling addVoiceCallDisposition.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-call-dispositions`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: voiceCallDisposition,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteVoiceCallDisposition(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteVoiceCallDisposition.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-call-dispositions/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVoiceCallDispositionByCode(code, observe = 'body', reportProgress = false, options) {
      if (code === null || code === undefined) {
        throw new Error('Required parameter code was null or undefined when calling getVoiceCallDispositionByCode.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-call-dispositions/by-code/${this.configuration.encodeParam({
        name: "code",
        value: code,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVoiceCallDispositionById(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getVoiceCallDispositionById.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-call-dispositions/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVoiceCallDispositionList(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-call-dispositions`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateVoiceCallDisposition(id, voiceCallDisposition, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateVoiceCallDisposition.');
      }
      if (voiceCallDisposition === null || voiceCallDisposition === undefined) {
        throw new Error('Required parameter voiceCallDisposition was null or undefined when calling updateVoiceCallDisposition.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/voice-call-dispositions/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: voiceCallDisposition,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function VoiceCallDispositionControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || VoiceCallDispositionControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(TelephonyDomainConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: VoiceCallDispositionControllerService,
        factory: VoiceCallDispositionControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return VoiceCallDispositionControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const APIS = [CountryControllerService, EmailContactControllerService, EmailMessageControllerService, LocalNumberControllerService, LocalNumberOverrideControllerService, MessageDirectionControllerService, MoVoicePinControllerService, OptInStatusTypeControllerService, PhoneContactControllerService, PhoneContactPreferenceControllerService, PhoneNumberTypeControllerService, SettingControllerService, SmsMessageControllerService, ThirdPartyNumberControllerService, VoiceCallControllerService, VoiceCallDispositionControllerService];

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * TelephonyDomainService
 * Domain service for the telephony schema
 *
 * The version of the OpenAPI document: 1.122.0-SNAPSHOT
 * Contact: #AVN-ProdEng-ProdExp-Cloud-Connectivity@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
let TelephonyDomainApiModule = /*#__PURE__*/(() => {
  class TelephonyDomainApiModule {
    static forRoot(configurationFactory) {
      return {
        ngModule: TelephonyDomainApiModule,
        providers: [{
          provide: TelephonyDomainConfiguration,
          useFactory: configurationFactory
        }]
      };
    }
    constructor(parentModule, http) {
      if (parentModule) {
        throw new Error('TelephonyDomainApiModule is already loaded. Import in your base AppModule only.');
      }
      if (!http) {
        throw new Error('You need to import the HttpClientModule in your AppModule! \n' + 'See also https://github.com/angular/angular/issues/20575');
      }
    }
    static {
      this.ɵfac = function TelephonyDomainApiModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TelephonyDomainApiModule)(i0.ɵɵinject(TelephonyDomainApiModule, 12), i0.ɵɵinject(i1.HttpClient, 8));
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: TelephonyDomainApiModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return TelephonyDomainApiModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TelephonyDomainModule = /*#__PURE__*/(() => {
  class TelephonyDomainModule {
    static {
      this.ɵfac = function TelephonyDomainModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TelephonyDomainModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: TelephonyDomainModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [{
          ngModule: TelephonyDomainApiModule,
          providers: [{
            provide: TelephonyDomainConfiguration,
            deps: [FLY_WEB_COMMON_CONFIG],
            useFactory: config => new TelephonyDomainConfiguration({
              basePath: `${ServicesBaseUrl[config.environment]}/telephonyDomainService`
            })
          }]
        }]
      });
    }
  }
  return TelephonyDomainModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { APIS, BASE_PATH, COLLECTION_FORMATS, CountryControllerService, EmailContactControllerService, EmailMessageControllerService, LocalNumberControllerService, LocalNumberOverrideControllerService, MessageDirectionControllerService, MoVoicePinControllerService, OptInStatusTypeControllerService, PhoneContactControllerService, PhoneContactPreferenceControllerService, PhoneNumberTypeControllerService, SettingControllerService, SmsMessageControllerService, TelephonyDomainApiModule, TelephonyDomainConfiguration, TelephonyDomainModule, ThirdPartyNumberControllerService, VoiceCallControllerService, VoiceCallDispositionControllerService };
